/* eslint-disable arrow-parens */
import React, { useState, useCallback, useEffect, useContext, useRef, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { Paper, Grid, Button, TextField } from "@material-ui/core";
import { trackPromise } from "react-promise-tracker";
import SendIcon from "@material-ui/icons/Send";
import "date-fns";

import { ADMIN_DASHBOARD_HEADER } from "../../../../../../../constants/caption/dashboardHeader";
import { DynamicForm } from "../../../../../../../@types/dynamicForm.types";
import { MemberTypes } from "../../../../../../../@types/components/member.types";
import useRouteParam from "../../../../../../../hooks/useRouteParam";
import { api } from "../../../../../../../utils/api";
import ROLES from "../../../../../../../constants/roles";
import { Validation } from "../../../../../../../@types/validation.types";
import ExtractRouteParams from "../../../../../../../utils/commonFunctions/extractRouteParams";
import SnackbarAPICall from "../../../../../../shared/snackbar/SnackbarAPICall";
import { SnackbarProps } from "../../../../../../../@types/snackbarAPI.types";
import { UIContext } from "../../../../../../../contexts";

import "../../../../../../../globalStyles/paperFormStyles.css";
import { Member } from "../../../../../../../@types/member.types";
import { EditMemberCredentialPayloadTypes } from "../../../../../../../utils/api/auth/editMemberCredentials/editMemberCredentials";

const EditMemberSettings: React.FC = (): JSX.Element => {
	const [editProfileForm, setEditProfileForm] = useState<DynamicForm>();
	const [hasEditMemberSettingsSubmitClick, setHasEditMemberSettingsSubmitClick] = useState(false);
	const [employee, setEmployee] = useState<any>({
		user_name: "",
		email: ""
	});
	const [validation, setValidation] = useState<Validation>({
		employee: {},
		status: "invalid"
	});
	const [snackbarAPICallProps, setSnackbarAPICallProps] = useState<SnackbarProps>({
		open: false,
		severity: undefined,
		message: "",
		handleSnackbarClose: (event, reason) => {
			if (reason === "clickaway") return;
			setSnackbarAPICallProps(
				Object.assign({}, snackbarAPICallProps, {
					open: false
				})
			);
		}
	});

	const querySSN = useRouteParam("qs");
	const queryGroupNumber = useRouteParam("gn");
	const { setDashboardHeader } = useContext(UIContext);
	const navigate = useNavigate();
	const _validation = useRef<Validation>();
	const routeParam = ExtractRouteParams();

	const handleValidation = useCallback(async () => {
		const { email } = employee;
		_validation.current = {
			...validation,
			employee: {},
			status: "invalid"
		};
		let flag = true;

		// employee validation checking
		if (email.length === 0) {
			_validation.current.employee["email"] = "Email Id is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}

		if (flag === true) _validation.current["status"] = "valid";
		else {
			_validation.current["status"] = "invalid";
			alert("Please, fill all required fields!");
		}
		setValidation(Object.assign({}, _validation.current));
		return _validation.current["status"];
	}, [employee, validation]);

	const handleEmployeeInformationChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { name, value } = event.target;

			setEmployee(Object.assign({}, employee, { [name]: value }));
		},
		[employee]
	);

	const getMembersByEmployeeSSN = useCallback(async (_employee_SSN: string, group_number: number) => {
		const findEmployeeQuery = {
			group_number,
			employee_SSN: _employee_SSN
		};

		const response = await trackPromise(api.member.getMembersByEmployeeSSN(findEmployeeQuery));

		if (response) {
			const { memberInstance } = response;
			memberInstance.forEach(async (member: MemberTypes) => {
				const { role } = member;
				if (role === ROLES.employee) {
					const { user_name, email } = member;

					const _member = {
						user_name,
						email
					};
					setEmployee(Object.assign({}, _member));
				}
			});
		}
	}, []);

	const handleRedirectToMemberDetails = useCallback(() => {
		const pathname = `/employer/member?${routeParam}`;
		navigate(pathname);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleEditMemberSettingsSubmitClick = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>) => {
			try {
				event.preventDefault();
				const validation = await handleValidation();
				if (validation === "valid") {
					const { email, user_name } = employee;

					const editMemberSettingsPayload: EditMemberCredentialPayloadTypes = {
						group_number: queryGroupNumber as number,
						SSN: querySSN as string,
						email: email as string,
						user_name: user_name as string
					};

					const response = await trackPromise(api.auth.editMemberCredentials(editMemberSettingsPayload));

					if (response) {
						setSnackbarAPICallProps(
							Object.assign({}, snackbarAPICallProps, {
								open: true,
								message: "Member Credentials Update Successful!",
								severity: "success"
							})
						);

						setTimeout(() => {
							handleRedirectToMemberDetails();
						}, 2000);
					}
				} else {
					setHasEditMemberSettingsSubmitClick(false);
				}
			} catch (error) {
				setHasEditMemberSettingsSubmitClick(false);
				alert("Profile Edit unsuccessful!");
				throw error;
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[employee, handleValidation]
	);

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.edit_member_settings);
	}, [setDashboardHeader]);

	useEffect(() => {
		if (querySSN && queryGroupNumber) getMembersByEmployeeSSN(querySSN as string, queryGroupNumber as number);
	}, [getMembersByEmployeeSSN, queryGroupNumber, querySSN]);

	useEffect(() => {
		setEditProfileForm(
			Object.assign(
				{},
				{
					"Member Setting Information": [
						{
							label: "Username",
							name: "user_name",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
								handleEmployeeInformationChange(event),
							value: employee.user_name,
							type: "textfield"
						},
						{
							label: "Email",
							name: "email",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
								handleEmployeeInformationChange(event),
							value: employee.email,
							type: "textfield"
						}
					]
				}
			)
		);
	}, [employee, handleEmployeeInformationChange]);

	return (
		<div className="edit-profile-information-container">
			<Suspense fallback={<div />}>
				<SnackbarAPICall snackbarProps={snackbarAPICallProps} />
			</Suspense>
			<div className="pf-action-button-container" id="pf-action-button-container">
				<Button
					className="button-green"
					onClick={handleEditMemberSettingsSubmitClick}
					variant="contained"
					disabled={hasEditMemberSettingsSubmitClick}
					style={{
						cursor: hasEditMemberSettingsSubmitClick ? "not-allowed" : "pointer",
						pointerEvents: "unset"
					}}
				>
					<span className="button-label-with-icon">Submit</span>
					<span>
						<SendIcon className="button-icon" />
					</span>
				</Button>
			</div>
			<Grid container spacing={1} className="pf-grid-container">
				{editProfileForm &&
					Object.entries(editProfileForm).map(([key, value], index) => {
						return (
							<div key={index}>
								<div className="pf-title">{key}</div>
								<Paper className="pf-paper-container" variant="outlined" elevation={3}>
									{value.map((field, index) => {
										const { name, label, value, type, onChange } = field;
										return (
											<Grid item key={index} xl={12} lg={12} md={12} sm={12} xs={12}>
												<Grid container spacing={1} className="pf-label">
													<Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
														<div className="pf-label-text" id="pf-label-text">
															{label}
														</div>
													</Grid>
													<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
														{type === "textfield" ? (
															<TextField
																className="input-text-field"
																value={value}
																name={name}
																onChange={onChange}
																variant="outlined"
																helperText={validation.employee[name]}
																style={{
																	pointerEvents: "unset",
																	width: "340px",
																	borderRadius: 50
																}}
															/>
														) : null}
													</Grid>
												</Grid>
											</Grid>
										);
									})}
								</Paper>
							</div>
						);
					})}
			</Grid>
		</div>
	);
};

export default EditMemberSettings;
